import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { generateAuditedClient } from "../audit/auditedClient";
import { listConversations } from "../graphql/queries";
import { ensureConversationIndex } from "../graphql/mutations";
import {
  ConversationSummary as ApiConversationSummary,
  listConversationsInput,
} from "../models/API";
import { hexToString } from "./utils/utils";

interface ConversationHistoryPanelProps {
  userId: number;
  currentConversationId: string;
  /**
   * Conversation id for the always-present "New Chat" entry. This is the
   * per-login-session id (derived from `userConfig.login_times`). The pinned
   * row stays at the top of the panel regardless of which conversation the
   * user has selected, so a fresh chat is always one click away.
   */
  newChatConversationId: string;
  onSelectConversation: (conversationId: string) => void;
  /**
   * Increment to signal the panel should refresh from the backend
   * (e.g. after the user asks a question so the title can show up).
   */
  refreshKey?: number;
  /**
   * When true, the panel renders inline (filling its parent's width) instead
   * of as a fixed-position right-side rail. Used when the panel lives inside
   * the left navigation drawer.
   */
  embedded?: boolean;
}

interface RecentConversation {
  conversation_id: string;
  title: string;
  latest_at: number;
}

const PANEL_WIDTH = 260;
const TITLE_MAX_LEN = 50;
// Server page size for the Recents list. The backend clamps to 100.
const PAGE_SIZE = 50;

// Filler phrases commonly prefixing a question. Stripping these makes the
// title in the Recents list lead with the actual topic.
const LEADING_FILLERS = [
  "please",
  "pls",
  "plz",
  "kindly",
  "can you please",
  "could you please",
  "would you please",
  "can you",
  "could you",
  "would you",
  "will you",
  "help me",
  "help me to",
  "i want to",
  "i would like to",
  "i'd like to",
  "i need to",
  "i'd like you to",
  "i want you to",
  "i need you to",
  "let's",
  "lets",
  "show me",
  "tell me",
  "give me",
  "i wonder",
  "just",
  "hi",
  "hey",
  "hello",
];

/**
 * Strip leading filler phrases like "can you please" / "i want to".
 * If stripping would empty the text (e.g. the user just typed "hi"), keep
 * the original so the panel still has something to show instead of falling
 * back to "Untitled".
 */
function stripLeadingFiller(text: string): string {
  let out = text;
  // Run multiple passes so "hey, can you please..." → "..." collapses fully.
  for (let i = 0; i < 3; i++) {
    let changed = false;
    const lower = out.toLowerCase();
    for (const phrase of LEADING_FILLERS) {
      if (
        lower.startsWith(phrase + " ") ||
        lower.startsWith(phrase + ",") ||
        lower === phrase
      ) {
        const next = out.slice(phrase.length).replace(/^[,\s]+/, "");
        // Don't strip if it would leave nothing meaningful behind.
        if (!next) return out;
        out = next;
        changed = true;
        break;
      }
    }
    if (!changed) break;
  }
  return out;
}

/**
 * Derive a concise, readable title from the conversation's first question.
 *
 * The value comes from the index header item's `title` attribute, which the backend
 * already truncated (on the decoded string, so multi-byte characters stay intact).
 */
function deriveTitle(rawQuestion: string | null | undefined): string {
  if (!rawQuestion) return "Untitled conversation";
  let text = rawQuestion;
  // Stored values are hex-encoded UTF-8 (see SPBFinnLambda save_conversation).
  // Fall back to the raw value if it isn't valid hex.
  if (/^[0-9a-fA-F]+$/.test(rawQuestion) && rawQuestion.length % 2 === 0) {
    try {
      text = hexToString(rawQuestion);
    } catch {
      /* keep raw */
    }
  }

  // Drop common markdown/formatting noise so titles read naturally.
  let cleaned = text
    // Code fences and inline backticks
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    // Markdown images / links -> keep just the label
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // Bold / italic / strike markers
    .replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, "$1")
    // Heading hashes / blockquote markers at the start of a line
    .replace(/^#+\s*/gm, "")
    .replace(/^>+\s*/gm, "")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "Untitled conversation";

  cleaned = stripLeadingFiller(cleaned);
  if (!cleaned) return "Untitled conversation";

  // Truncate at a word boundary when possible for cleaner visuals.
  if (cleaned.length > TITLE_MAX_LEN) {
    const slice = cleaned.slice(0, TITLE_MAX_LEN - 1);
    const lastSpace = slice.lastIndexOf(" ");
    cleaned =
      (lastSpace > TITLE_MAX_LEN - 15 ? slice.slice(0, lastSpace) : slice) +
      "…";
  }

  // Capitalize the first letter so titles read like, well, titles.
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  // Strip a trailing question mark — most entries are questions; keeping it
  // clutters the list. Other punctuation (".", "!") is preserved.
  cleaned = cleaned.replace(/\?+$/, "");

  return cleaned;
}

/**
 * Map index rows to list entries, preserving the order the server returned.
 *
 * `listConversations` reads the sparse conversation index, so it already yields one
 * row per conversation sorted by `last_modified_at` descending. This replaces a
 * client-side group-and-sort over every turn item, which could only order what it
 * received: when the response was cut off at DynamoDB's 1MB page limit, a partially
 * returned conversation got an understated "latest" timestamp and ranked wrong, and
 * conversations past the cut were missing entirely with nothing to signal it.
 */
function toRecentConversations(
  items: (ApiConversationSummary | null)[]
): RecentConversation[] {
  const recents: RecentConversation[] = [];
  for (const item of items) {
    // Prefer session_id (the thread id getConversation expects) over the header
    // item's raw "HDR#<session_id>" sort key.
    const sessionId = item?.session_id || item?.conversation_id?.split("#")[1];
    if (!sessionId) continue;
    recents.push({
      conversation_id: sessionId,
      title: deriveTitle(item?.title),
      latest_at: Number(item?.last_modified_at) || 0,
    });
  }
  return recents;
}

const ConversationHistoryPanel: React.FC<ConversationHistoryPanelProps> = ({
  userId,
  currentConversationId,
  newChatConversationId,
  onSelectConversation,
  refreshKey = 0,
  embedded = false,
}) => {
  const [conversations, setConversations] = useState<RecentConversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Non-null means the server has more conversations than were returned. Surfaced as a
  // "Load more" row so a long history is never silently cut short.
  const [nextToken, setNextToken] = useState<string | null>(null);
  // Which user's index backfill has already been requested this mount, so the
  // once-per-user migration is not re-issued on every refreshKey change.
  const backfilledForUserRef = useRef<number | null>(null);

  /**
   * Ensure conversations written before the index existed have header items, so they
   * still appear in the list. Idempotent and cheap after the first run (a single
   * GetItem against a per-user sentinel). Failure is non-fatal: whatever is already
   * indexed still renders.
   */
  const ensureIndex = useCallback(
    async (client: ReturnType<typeof generateAuditedClient>) => {
      if (backfilledForUserRef.current === userId) return;
      try {
        await client.graphql({
          query: ensureConversationIndex,
          variables: { input: { user_id: userId } },
        });
        backfilledForUserRef.current = userId;
      } catch (err) {
        console.error(
          "[ConversationHistoryPanel] Failed to ensure conversation index:",
          err
        );
      }
    },
    [userId]
  );

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const client = generateAuditedClient();
      await ensureIndex(client);
      const input: listConversationsInput = {
        user_id: userId,
        limit: PAGE_SIZE,
      };
      const result = await client.graphql({
        query: listConversations,
        variables: { input },
      });
      const page = result.data.listConversations;
      setConversations(toRecentConversations(page?.items ?? []));
      setNextToken(page?.nextToken ?? null);
    } catch (err) {
      console.error("[ConversationHistoryPanel] Failed to load history:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, ensureIndex]);

  const loadMore = useCallback(async () => {
    if (!userId || !nextToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const client = generateAuditedClient();
      const input: listConversationsInput = {
        user_id: userId,
        limit: PAGE_SIZE,
        nextToken,
      };
      const result = await client.graphql({
        query: listConversations,
        variables: { input },
      });
      const page = result.data.listConversations;
      // Append: pages arrive in descending last_modified_at order, so concatenating
      // preserves the overall ordering.
      setConversations((prev) => [
        ...prev,
        ...toRecentConversations(page?.items ?? []),
      ]);
      setNextToken(page?.nextToken ?? null);
    } catch (err) {
      console.error("[ConversationHistoryPanel] Failed to load more:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, nextToken, loadingMore]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshKey]);

  // "New Chat" is always pinned at the top. The underlying id is the current
  // login session's conversation id, so once the user asks a question in it
  // the backend starts writing rows under that prefix. We still render it as
  // "New Chat" (and filter the matching entry out of history below) so the
  // row is always available, even after the user navigates to an older
  // conversation.
  const historyExcludingNewChat = React.useMemo(
    () =>
      conversations.filter((c) => c.conversation_id !== newChatConversationId),
    [conversations, newChatConversationId]
  );

  const containerSx = embedded
    ? {
        flex: 1,
        minHeight: 0, // allow the inner scroll region to flex correctly
        display: "flex",
        flexDirection: "column" as const,
        overflow: "hidden",
        backgroundColor: "transparent",
      }
    : {
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        borderLeft: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column" as const,
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "grey.50",
        boxShadow: "-2px 0 8px rgba(0,0,0,0.04)",
        position: "fixed" as const,
        right: 0,
        top: 0,
        zIndex: 1,
      };

  return (
    <Box sx={containerSx}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          color="text.secondary"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "0.7rem",
          }}
        >
          Recents
        </Typography>
      </Box>
      <Divider sx={{ mx: 1.5 }} />
      <Box sx={{ flexGrow: 1, overflow: "auto", p: 1.5, pt: 1 }}>
        {loading && conversations.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : (
          <List dense disablePadding>
            {/* Pinned "New Chat" entry — always present, always on top. */}
            <ListItemButton
              key={`new-chat-${newChatConversationId}`}
              selected={currentConversationId === newChatConversationId}
              onClick={() => onSelectConversation(newChatConversationId)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 0.75,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "action.hover",
                  transform: "translateX(-2px)",
                },
                "&.Mui-selected": {
                  backgroundColor: "primary.50",
                  borderLeft: "3px solid",
                  borderLeftColor: "primary.main",
                  "&:hover": {
                    backgroundColor: "primary.100",
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <ChatBubbleOutlineIcon
                  sx={{ color: "#1565c0", fontSize: 18 }}
                />
              </ListItemIcon>
              <ListItemText
                primary="New Chat"
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight:
                    currentConversationId === newChatConversationId ? 600 : 500,
                }}
              />
            </ListItemButton>

            {historyExcludingNewChat.length === 0 && (
              <Box sx={{ px: 1, py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  No past conversations yet.
                </Typography>
              </Box>
            )}

            {historyExcludingNewChat.map((c) => {
              const isSelected = c.conversation_id === currentConversationId;
              return (
                <ListItemButton
                  key={c.conversation_id}
                  selected={isSelected}
                  onClick={() => onSelectConversation(c.conversation_id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    py: 0.75,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      transform: "translateX(-2px)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "primary.50",
                      borderLeft: "3px solid",
                      borderLeftColor: "primary.main",
                      "&:hover": {
                        backgroundColor: "primary.100",
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ChatBubbleOutlineIcon
                      sx={{ color: "#1565c0", fontSize: 18 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={c.title}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: isSelected ? 600 : 400,
                      sx: {
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.3,
                      },
                    }}
                  />
                </ListItemButton>
              );
            })}

            {/* Shown whenever the server reported more conversations than were
                returned, so a long history is visibly paginated rather than
                silently truncated. */}
            {nextToken && (
              <ListItemButton
                onClick={loadMore}
                disabled={loadingMore}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 0.75,
                  justifyContent: "center",
                }}
              >
                {loadingMore ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography variant="caption" color="primary">
                    Load more
                  </Typography>
                )}
              </ListItemButton>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ConversationHistoryPanel;
